import React, { useEffect, useState } from 'react'
import { useIntersectionObserverLoadMore } from 'hooks/useIntersectionObserverLoadMore'
import Image from 'next/legacy/image'
import { ResponsiveDialogDrawer } from 'component_library/Lib_Design_System/block_components/responsiveDialogDrawer'
import Icon from 'common/Icon'
import credentials from 'common/ProviderCredentials/credentials'
import { TESTIMONIALS_DISCLAIMER_TEXT } from 'app/ProviderDetails/constants'
import { If } from '@/utility_components/ControlStatements'
import {
  getProviderTitle,
  getTestimonialAttribution,
} from 'app/ProviderDetails/utils'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export const TESTIMONIAL_CARD_ID_PREFIX = 'testimonial-sheet-'

const scrollElementWithinContainer = (
  container: HTMLElement,
  element: HTMLElement,
  behavior: ScrollBehavior = 'smooth'
) => {
  const top =
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  container.scrollTo({ top, behavior })
}

type TestimonialsBottomSheetProps = {
  show: boolean
  onHide: () => void
  items: ProviderTestimonialItem[]
  providerDetails: Provider
  scrollToTestimonialId: string | null
  loadMore?: () => Promise<void>
  hasMore?: boolean
  isLoading?: boolean
}

const TestimonialsBottomSheet: React.FC<TestimonialsBottomSheetProps> = ({
  show,
  onHide,
  items,
  providerDetails,
  scrollToTestimonialId,
  loadMore,
  hasMore = false,
  isLoading = false,
}) => {
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null)

  const { firstName, lastName, profileImagePath, filters } =
    providerDetails ?? {}
  const providerFirstName = firstName ?? ''
  const titleAbbreviation = getProviderTitle(providerDetails)
  const title = credentials[titleAbbreviation]?.definition ?? titleAbbreviation
  const genderPronouns = (filters ?? []).filter(
    (f) => f.type === 'gender_pronoun'
  )
  const pronounLine =
    genderPronouns.length > 0
      ? `  •  ${genderPronouns.map((p) => p.value).join('/')}`
      : ''
  const titleLine = `${title}${pronounLine}`

  const { sentinelRef } = useIntersectionObserverLoadMore({
    rootScroll: scrollRoot,
    active: show,
    hasMore,
    isLoading,
    loadMore,
  })

  useEffect(() => {
    if (!show || !scrollToTestimonialId || !scrollRoot) {
      return
    }

    const id = `${TESTIMONIAL_CARD_ID_PREFIX}${scrollToTestimonialId}`
    const el = document.getElementById(id)

    if (el) {
      const timer = setTimeout(() => {
        scrollElementWithinContainer(scrollRoot, el)
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [show, scrollToTestimonialId, scrollRoot])

  return (
    <ResponsiveDialogDrawer
      open={show}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onHide()
        }
      }}
      drawerBreakpoint="mobile"
      drawerDismissible
      drawerShouldScaleBackground={false}
      drawerCloseIconVisible={false}
      classNames={{
        drawerOverlayClassName: '!~bg-black/20',
        drawerTopFill: '~hidden',
        drawerContent:
          '!~mt-0 !~bottom-0 !~h-[90dvh] !~max-h-[90dvh] ~z-[1100] ~flex ~min-h-0 ~w-full ~max-w-none ~flex-col ~overflow-hidden ~rounded-t-[30px] ~border-0 !~bg-white ~p-0 ~shadow-[0_8px_24px_rgba(33,37,41,0.24)]',
        drawerHandle: '~hidden',
        drawerContentWrapper:
          '~flex ~h-full ~min-h-0 ~flex-1 ~flex-col ~overflow-hidden ~p-0',
      }}
    >
      <div className="~flex ~h-full ~min-h-0 ~flex-col ~overflow-hidden">
        <div className="~shrink-0 ~flex ~flex-col ~gap-8">
          <header
            className={cn(
              '~relative ~flex ~w-full ~flex-col ~gap-0 ~p-5 ~px-6 ~bg-latte-50',
              '~rounded-t-[30px]'
            )}
          >
            <button
              type="button"
              className={cn(
                '~absolute ~top-4 ~right-[17px] ~p-0 ~m-0 ~bg-transparent ~border-0',
                '~cursor-pointer ~leading-none ~rounded-3xs',
                'hover:~opacity-80',
                '~outline-none ~focus-visible:~outline-2 ~focus-visible:~outline-sky ~focus-visible:~outline-offset-2'
              )}
              onClick={onHide}
              aria-label="Close testimonials"
            >
              <Icon icon="close" fill="black" size="24" aria-hidden />
            </button>
            <div className="~flex ~items-center ~gap-4">
              <div className="~relative ~shrink-0 ~size-[72px] ~rounded ~overflow-hidden ~border-[5px] ~border-white">
                <Image
                  layout="fill"
                  src={
                    profileImagePath
                      ? profileImagePath
                      : '/images/default_provider.svg'
                  }
                  alt=""
                  className="~object-cover ~object-top"
                />
              </div>
              <div className="~flex ~flex-col ~gap-0.5 ~min-w-0">
                <h2 className="~font-mrseavesxl ~text-2xl ~font-bold ~leading-[30px] ~text-black ~m-0">
                  {[firstName, lastName].filter(Boolean).join(' ').trim()}
                </h2>
                <p className="~font-gibson ~text-xs ~font-normal ~text-dim-gray ~m-0">
                  {titleLine}
                </p>
              </div>
            </div>
          </header>

          <p className="~px-6 ~font-gibson ~text-base ~font-normal ~text-black ~m-0">
            {TESTIMONIALS_DISCLAIMER_TEXT}
          </p>
        </div>

        <div
          ref={setScrollRoot}
          className={cn(
            '~flex ~min-h-0 ~flex-1 ~flex-col ~gap-0 ~overflow-y-auto ~overscroll-y-contain',
            '~px-6'
          )}
        >
          <div className="~flex ~flex-col ~divide-y ~divide-grey-wash">
            {items.map((item) => (
              <div
                key={item.id}
                id={`${TESTIMONIAL_CARD_ID_PREFIX}${item.id}`}
                className="~flex ~flex-col ~py-8"
              >
                <div className="~shrink-0 ~h-10 ~w-[43px] ~mb-6">
                  <img
                    src="/static/quote-icon.svg"
                    alt=""
                    className="~block ~h-10 ~w-auto ~max-w-[43px] ~object-contain ~object-left-top"
                    width={43}
                    height={40}
                    aria-hidden
                  />
                </div>
                <div className="~flex-1">
                  <p className="~font-gibson ~text-xl ~font-normal ~text-black ~m-0 ~whitespace-pre-wrap">
                    {item.reviewBody?.trim() ?? ''}
                  </p>
                </div>
                <p className="~font-gibson ~text-sm ~font-normal ~text-dim-gray ~m-0 ~mt-2">
                  {getTestimonialAttribution(item, providerFirstName)}
                </p>
              </div>
            ))}
          </div>
          <If condition={hasMore}>
            <div
              ref={sentinelRef}
              className="~min-h-px ~w-full ~pointer-events-none"
            />
          </If>
          <If condition={isLoading}>
            <div className="~flex ~justify-center ~items-center ~p-4">
              <Loader2 className="~size-6 ~animate-spin" aria-hidden />
            </div>
          </If>
        </div>
      </div>
    </ResponsiveDialogDrawer>
  )
}

export default TestimonialsBottomSheet
